// One safe markdown renderer for the whole app. marked 12 passes raw HTML in the
// source straight through, and we render the result via dangerouslySetInnerHTML —
// so AI-assistant output (and, defensively, our own docs) could otherwise inject
// <script>/<img onerror=…> or javascript:-scheme links. We (1) escape raw-HTML
// tokens to text and (2) sanitize link/image targets to safe schemes only.
// No dependency, no bundle cost — proportionate for a single-owner app.
import { marked } from "marked";

const esc = (s) => String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Allow only http(s) / mailto / relative / anchor URLs. Block javascript:, data:,
// vbscript:, file:, … so a crafted link/image in model output can't execute JS.
function safeHref(href) {
  const raw = String(href ?? "");
  // drop control chars + whitespace (0x00–0x20) that can hide a scheme, then check it
  const probe = raw.toLowerCase().replace(/[ - ]+/g, "");
  const scheme = probe.match(/^([a-z][a-z0-9+.-]*):/);     // a URL scheme, if any
  if (scheme && !["http", "https", "mailto"].includes(scheme[1])) return "#";
  return raw;
}

marked.use({
  // sanitize link/image targets at the token level, BEFORE the default renderer
  // builds the <a>/<img> — no need to rebuild the HTML ourselves.
  walkTokens(token) {
    if (token.type === "link" || token.type === "image") token.href = safeHref(token.href);
  },
  renderer: {
    // block- and inline-level raw HTML both arrive here (string in older marked,
    // token object in 12+); escape whichever shape we get.
    html(token) { return esc(typeof token === "string" ? token : (token?.text ?? token?.raw ?? "")); },
  },
});

export function renderMarkdown(text, opts = {}) {
  return marked.parse(String(text ?? ""), { gfm: true, breaks: false, ...opts });
}
